class LaciniaOfferChooser extends HTMLElement {
  #quantityInput;
  #buttons;
  #summary;
  #shippingNote;
  #formatter;

  connectedCallback() {
    try {
      const form = this.closest('form[action*="/cart/add"]') || this.closest('form');
      this.#quantityInput = form?.querySelector('input[name="quantity"]');
      this.#buttons = Array.from(this.querySelectorAll('[data-offer-quantity]'));
      this.#summary = this.querySelector('[data-offer-summary]');
      this.#shippingNote = this.querySelector('[data-offer-shipping-note]');

      if (!this.#quantityInput || !this.#buttons.length || !this.#summary) return;

      const locale = this.dataset.locale || document.documentElement.lang || 'tr-TR';
      const currency = this.dataset.currency || 'TRY';
      this.#formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
      });

      this.#buttons.forEach((button) => {
        button.disabled = button.dataset.offerAvailable !== 'true';
        button.addEventListener('click', this.#handleOfferClick);
      });
      this.#quantityInput.addEventListener('input', this.#handleQuantityChange);
      this.#quantityInput.addEventListener('change', this.#handleQuantityChange);

      this.dataset.ready = 'true';

      const requestedOffer = new URL(window.location.href).searchParams.get('offer');
      const requestedButton = requestedOffer === '3'
        ? this.#buttons.find((button) => button.dataset.offerQuantity === '3' && !button.disabled)
        : null;

      if (requestedButton) {
        this.#setQuantity(3);
      } else {
        this.#syncFromInput();
      }
    } catch (error) {
      console.warn('[lacinia-offer-chooser] Native quantity selector remains available.', error);
    }
  }

  disconnectedCallback() {
    this.#buttons?.forEach((button) => button.removeEventListener('click', this.#handleOfferClick));
    this.#quantityInput?.removeEventListener('input', this.#handleQuantityChange);
    this.#quantityInput?.removeEventListener('change', this.#handleQuantityChange);
  }

  #handleOfferClick = (event) => {
    const quantity = Number.parseInt(event.currentTarget.dataset.offerQuantity, 10);
    if (Number.isFinite(quantity)) this.#setQuantity(quantity);
  };

  #handleQuantityChange = () => this.#syncFromInput();

  #setQuantity(quantity) {
    if (!this.#quantityInput) return;

    this.#quantityInput.value = String(quantity);
    this.#quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.#quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.#syncFromInput();
  }

  #syncFromInput() {
    if (!this.#quantityInput || !this.#summary) return;

    const quantity = Number.parseInt(this.#quantityInput.value, 10);
    const isValidQuantity = Number.isFinite(quantity) && quantity > 0;

    this.#buttons.forEach((button) => {
      const isSelected = isValidQuantity && Number.parseInt(button.dataset.offerQuantity, 10) === quantity;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });

    if (!isValidQuantity) {
      this.#summary.textContent = 'Geçerli bir adet girin';
      if (this.#shippingNote) this.#shippingNote.textContent = 'Sepete eklemeden önce adedi kontrol edin.';
      return;
    }

    const unitPrice = Number.parseInt(this.dataset.unitPrice, 10);
    const fixedMoney = quantity === 1 ? this.dataset.moneyOne : quantity === 3 ? this.dataset.moneyThree : '';
    const selectionTotal = unitPrice * quantity;
    const formattedTotal = fixedMoney || this.#formatMoney(selectionTotal);
    this.#summary.textContent = `${quantity} adet · ${formattedTotal}`;

    if (this.#shippingNote) {
      const freeShippingThreshold = Number.parseInt(this.dataset.freeShippingThreshold, 10);
      this.#shippingNote.textContent = Number.isFinite(selectionTotal)
        && Number.isFinite(freeShippingThreshold)
        && selectionTotal >= freeShippingThreshold
        ? 'Bu seçim ücretsiz kargo eşiğini geçer.'
        : 'Kargo, sepet toplamına göre uygulanır.';
    }
  }

  #formatMoney(amountInMinorUnits) {
    if (!Number.isFinite(amountInMinorUnits)) return '';
    if (this.#formatter) return this.#formatter.format(amountInMinorUnits / 100);
    return `${(amountInMinorUnits / 100).toFixed(2)} ${this.dataset.currency || 'TRY'}`;
  }
}

if (!customElements.get('lacinia-offer-chooser')) {
  customElements.define('lacinia-offer-chooser', LaciniaOfferChooser);
}
